import { Component, forwardRef, ReactNode } from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";

interface SafeHCaptchaProps {
  sitekey: string;
  onVerify: (token: string) => void;
  onLoad?: () => void;
  onExpire?: () => void;
  onError?: (err: string) => void;
  size?: "normal" | "compact" | "invisible";
  loadAsync?: boolean;
}

const MAX_RETRY_ATTEMPTS = 5;
const RETRY_DELAY_MS = 2000;

// Error boundary to catch HCaptcha rendering errors — auto-retries instead of silently skipping
class HCaptchaErrorBoundary extends Component<
  { children: ReactNode; onError?: () => void },
  { hasError: boolean; retryCount: number }
> {
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: any) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  static getDerivedStateFromError(error: any) {
    console.error('[HCaptchaErrorBoundary] getDerivedStateFromError:', error?.message);
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error('[HCaptchaErrorBoundary] componentDidCatch error:', error);
    console.error('[HCaptchaErrorBoundary] ErrorInfo componentStack:', errorInfo?.componentStack);

    // Auto-retry after a delay if we haven't exceeded max attempts
    if (this.state.retryCount < MAX_RETRY_ATTEMPTS) {
      console.log(`[HCaptchaErrorBoundary] Scheduling retry ${this.state.retryCount + 1}/${MAX_RETRY_ATTEMPTS} in ${RETRY_DELAY_MS}ms`);
      this.retryTimer = setTimeout(() => {
        this.setState(prev => ({
          hasError: false,
          retryCount: prev.retryCount + 1,
        }));
      }, RETRY_DELAY_MS);
    } else {
      console.error('[HCaptchaErrorBoundary] Max retries exceeded, notifying parent');
      this.props.onError?.();
    }
  }

  componentWillUnmount() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }
  }

  render() {
    if (this.state.hasError) {
      // Show nothing while waiting for retry — captcha will re-mount shortly
      return null;
    }
    return this.props.children;
  }
}

/**
 * A safe wrapper around HCaptcha that handles iOS-specific issues
 * and provides graceful error handling with an error boundary.
 * The error boundary auto-retries rendering up to MAX_RETRY_ATTEMPTS times
 * so the captcha cannot be silently skipped.
 */
export const SafeHCaptcha = forwardRef<any, SafeHCaptchaProps>((props, ref) => {
  const handleError = (err: string) => {
    console.error('[SafeHCaptcha] Error:', err);
    props.onError?.(err);
  };

  return (
    <HCaptchaErrorBoundary onError={() => handleError('render-error')}>
      <div style={{ position: 'absolute', left: '-9999px', contain: 'layout style paint' }}>
        <HCaptcha
          ref={ref}
          {...props}
          onError={handleError}
        />
      </div>
    </HCaptchaErrorBoundary>
  );
});

SafeHCaptcha.displayName = 'SafeHCaptcha';
